import React, { useState, useEffect, useRef } from 'react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { X, Send, CheckCircle2, AlertCircle, MinusCircle, Mail, Loader2 } from 'lucide-react';
import { invitationApi } from '@/lib/api/invitationApi';
import { userSearchApi, UserSearchResult } from '@/lib/api/userSearchApi';
import { BatchSendInvitationResponse, BatchInviteStatus, BatchInviteStatusLabels } from '@/lib/models/invitation';
import { ActionButton } from '@/components/custom-ui/action-button';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';

interface SendInvitationModalProps {
    onClose: () => void;
    onSuccess: () => void;
}

// One resolved recipient. `user` is filled asynchronously if the email maps to an
// existing account (→ rich card); otherwise it's a new email (→ plain email card).
// `resolving` drives a brief spinner while we look it up.
interface Recipient {
    email: string;
    user?: UserSearchResult;
    resolving?: boolean;
}

// Basic client-side email shape check — the backend is authoritative; this just
// keeps malformed tokens out of the recipient list.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendInvitationModal: React.FC<SendInvitationModalProps> = ({ onClose, onSuccess }) => {
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Recipient picker + typeahead
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [input, setInput] = useState('');
    const [invalid, setInvalid] = useState<string[]>([]);
    const [suggestions, setSuggestions] = useState<UserSearchResult[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    const [batchResults, setBatchResults] = useState<BatchSendInvitationResponse | null>(null);

    // ── Typeahead search ──────────────────────────────────────────────────
    const runSearch = (value: string) => {
        if (searchTimeout) clearTimeout(searchTimeout);
        if (value.length >= 2) {
            const timeout = setTimeout(async () => {
                const results = await userSearchApi.searchUsers(value);
                setSuggestions(results);
                setShowSuggestions(results.length > 0);
                setHighlightedIndex(-1);
            }, 300);
            setSearchTimeout(timeout);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // ── Recipient management ──────────────────────────────────────────────
    const resolveRecipient = async (targetEmail: string) => {
        try {
            const results = await userSearchApi.searchUsers(targetEmail);
            const match = results.find((u) => (u.email || '').toLowerCase() === targetEmail);
            setRecipients((prev) =>
                prev.map((r) => (r.email === targetEmail ? { ...r, user: match, resolving: false } : r)),
            );
        } catch {
            setRecipients((prev) =>
                prev.map((r) => (r.email === targetEmail ? { ...r, resolving: false } : r)),
            );
        }
    };

    const addRecipientEmail = (raw: string) => {
        const targetEmail = raw.trim().toLowerCase();
        if (!targetEmail) return;
        if (!EMAIL_RE.test(targetEmail)) {
            setInvalid((prev) => (prev.includes(targetEmail) ? prev : [...prev, targetEmail]));
            return;
        }
        setRecipients((prev) => {
            if (prev.some((r) => r.email === targetEmail)) return prev;
            return [...prev, { email: targetEmail, resolving: true }];
        });
        resolveRecipient(targetEmail);
    };

    const addRecipientUser = (user: UserSearchResult) => {
        const targetEmail = (user.email || '').trim().toLowerCase();
        if (!targetEmail) return;
        setRecipients((prev) =>
            prev.some((r) => r.email === targetEmail) ? prev : [...prev, { email: targetEmail, user }],
        );
        setInput('');
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const removeRecipient = (targetEmail: string) => {
        setRecipients((prev) => prev.filter((r) => r.email !== targetEmail));
    };

    const handleInputChange = (value: string) => {
        setError(null);
        // Peel complete tokens (anything before a separator) into recipients;
        // keep the trailing fragment in the input for continued typing/typeahead.
        if (/[\s,;]/.test(value)) {
            const parts = value.split(/[\s,;]+/);
            const remainder = parts.pop() ?? '';
            parts.filter(Boolean).forEach(addRecipientEmail);
            setInput(remainder);
            runSearch(remainder);
            return;
        }
        setInput(value);
        runSearch(value);
    };

    // Paste is different from typing: the LAST token is complete too (there's no
    // trailing keystroke coming), so commit every token — including the final one.
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text');
        if (!/[\s,;]/.test(text)) return; // single token — let normal typing flow handle it
        e.preventDefault();
        setError(null);
        (input + text)
            .split(/[\s,;]+/)
            .filter(Boolean)
            .forEach(addRecipientEmail);
        setInput('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (showSuggestions && suggestions.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
                    return;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
                    return;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                        addRecipientUser(suggestions[highlightedIndex]);
                    } else if (input.trim()) {
                        addRecipientEmail(input);
                        setInput('');
                    }
                    return;
                case 'Escape':
                    setShowSuggestions(false);
                    setHighlightedIndex(-1);
                    return;
            }
        }
        // Enter with no open suggestion commits the typed email; Backspace on an
        // empty input pops the last recipient (fast list editing).
        if (e.key === 'Enter') {
            e.preventDefault();
            if (input.trim()) {
                addRecipientEmail(input);
                setInput('');
            }
        } else if (e.key === 'Backspace' && input === '' && recipients.length > 0) {
            removeRecipient(recipients[recipients.length - 1].email);
        }
    };

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && suggestionsRef.current) {
            const items = suggestionsRef.current.querySelectorAll('[data-suggestion]');
            items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    // Close suggestions on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (suggestionsRef.current && !suggestionsRef.current.contains(target) &&
                inputRef.current && !inputRef.current.contains(target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        // Commit any half-typed email still in the input before sending.
        const pending = input.trim().toLowerCase();
        const emails = recipients.map((r) => r.email);
        if (pending && EMAIL_RE.test(pending) && !emails.includes(pending)) {
            emails.push(pending);
        }
        if (emails.length === 0) {
            setError('Lägg till minst en mottagare.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const res = await invitationApi.sendInvitationsBatch({
                emails,
                message: message.trim() || undefined,
            });
            setBatchResults(res);
        } catch (err: any) {
            setError(err?.message || 'Något gick fel. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // "Bjud in fler" from the results view — reset to an empty form.
    const reset = () => {
        setBatchResults(null);
        setRecipients([]);
        setInput('');
        setInvalid([]);
        setError(null);
    };

    const getInitials = (user: UserSearchResult) => {
        const first = user.first_name?.[0] || '';
        const last = user.last_name?.[0] || '';
        return (first + last).toUpperCase() || '?';
    };

    const getDisplayName = (user: UserSearchResult) => {
        const parts = [user.first_name, user.last_name].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : user.email || 'Okänd användare';
    };

    // ── Results view (after send) ─────────────────────────────────────────
    if (batchResults) {
        const statusStyle: Record<BatchInviteStatus, { icon: React.ElementType; color: string; bg: string }> = {
            sent: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
            already_member: { icon: MinusCircle, color: 'text-gray-500', bg: 'bg-gray-50' },
            role_not_allowed: { icon: MinusCircle, color: 'text-gray-500', bg: 'bg-gray-50' },
            invalid: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
            failed: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
        };
        const { sent, skipped, invalid: invalidCount, failed } = batchResults;
        return (
            <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
                <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                    <h2 className="text-lg font-bold text-gray-900">Resultat</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="flex flex-wrap gap-2 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 font-medium">
                            <CheckCircle2 className="w-4 h-4" /> {sent} inbjudna
                        </span>
                        {skipped > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 font-medium">
                                <MinusCircle className="w-4 h-4" /> {skipped} hoppades över
                            </span>
                        )}
                        {invalidCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 font-medium">
                                <AlertCircle className="w-4 h-4" /> {invalidCount} ogiltiga
                            </span>
                        )}
                        {failed > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 font-medium">
                                <AlertCircle className="w-4 h-4" /> {failed} misslyckades
                            </span>
                        )}
                    </div>

                    <div className="border border-gray-100 rounded-xl divide-y divide-gray-50">
                        {batchResults.results.map((r) => {
                            const s = statusStyle[r.status];
                            const Icon = s.icon;
                            return (
                                <div key={r.email} className="flex items-center gap-3 px-4 py-2.5">
                                    <Icon className={`w-4 h-4 flex-shrink-0 ${s.color}`} />
                                    <span className="text-sm text-gray-900 flex-1 truncate">{r.email}</span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${s.bg} ${s.color}`} title={r.message}>
                                        {BatchInviteStatusLabels[r.status]}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0">
                    <button onClick={reset} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl">
                        Bjud in fler
                    </button>
                    <ActionButton icon={CheckCircle2} onClick={onSuccess}>
                        Klar
                    </ActionButton>
                </div>
            </IslandSlideOver>
        );
    }

    const count = recipients.length;

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-lg font-bold text-gray-900">Bjud in medarbetare</h2>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="p-3 bg-[#A4C3B2]/30 border border-[#A4C3B2] rounded-xl">
                    <p className="text-xs text-[#212121]">
                        Sök efter befintliga användare eller ange nya e-postadresser — en i taget eller klistra in en hel lista (kommatecken, mellanslag eller radbrytning). Varje mottagare visas nedan.
                    </p>
                </div>

                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lägg till mottagare</label>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onPaste={handlePaste}
                        onKeyDown={handleKeyDown}
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        placeholder="Sök namn, eller ange/klistra in e-postadresser..."
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        autoComplete="off"
                        autoFocus
                    />

                    {showSuggestions && (
                        <div
                            ref={suggestionsRef}
                            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
                        >
                            {suggestions.map((user, index) => (
                                <button
                                    key={user.id}
                                    data-suggestion
                                    onClick={() => addRecipientUser(user)}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                    className={`w-full flex items-center gap-3 p-3 transition-colors text-left ${
                                        index === highlightedIndex ? 'bg-[#A4C3B2]/30' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    {user.picture_url ? (
                                        <img
                                            src={resolveMediaUrl(user.picture_url)}
                                            alt=""
                                            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-xs flex-shrink-0">
                                            {getInitials(user)}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{getDisplayName(user)}</p>
                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {invalid.length > 0 && (
                    <div className="flex items-start gap-2 text-xs text-amber-700">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Hoppade över ogiltiga adresser: {invalid.join(', ')}.</span>
                        <button onClick={() => setInvalid([])} className="ml-auto text-amber-600 hover:underline flex-shrink-0">Rensa</button>
                    </div>
                )}

                {/* Resolved recipient cards */}
                {recipients.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-500">{recipients.length} mottagare</p>
                        {recipients.map((r) => (
                            <div key={r.email} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl">
                                {r.user?.picture_url ? (
                                    <img src={resolveMediaUrl(r.user.picture_url)} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                ) : r.user ? (
                                    <div className="w-10 h-10 rounded-full bg-[#A4C3B2] flex items-center justify-center text-[#1A4D4F] font-bold text-xs flex-shrink-0">
                                        {getInitials(r.user)}
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    {r.user ? (
                                        <>
                                            <p className="text-sm font-semibold text-gray-900 truncate">{getDisplayName(r.user)}</p>
                                            <p className="text-xs text-gray-500 truncate">{r.email}</p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm font-medium text-gray-900 truncate">{r.email}</p>
                                            <p className="text-xs text-gray-400">
                                                {r.resolving ? (
                                                    <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Söker konto…</span>
                                                ) : 'Ny inbjudan (inget konto ännu)'}
                                            </p>
                                        </>
                                    )}
                                </div>
                                {r.user?.role && (
                                    <span className="text-xs text-[#1A4D4F] capitalize flex-shrink-0">{r.user.role}</span>
                                )}
                                <button
                                    onClick={() => removeRecipient(r.email)}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Personligt meddelande (valfritt)</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Välkommen till teamet! Vi ser fram emot att..."
                        className="w-full h-24 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                    />
                </div>

                {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl">
                    Avbryt
                </button>
                <ActionButton icon={Send} onClick={handleSubmit} disabled={isSubmitting || count === 0}>
                    {isSubmitting ? 'Skickar...' : `Bjud in ${count || ''}`.trim()}
                </ActionButton>
            </div>
        </IslandSlideOver>
    );
};

export default SendInvitationModal;
